import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';

import type { BulkEnquiryItem } from './bulk-enquiry-state';

interface SelectedItemMediaProps {
    item: BulkEnquiryItem;
    /** Shape + size of the frame (e.g. `size-10 rounded-full`). */
    className?: string;
}

/**
 * Deliberately far larger than any of these thumbnails render at (~30–56px).
 * The source photos are landscape, so filling a square frame crops *and* scales
 * them up; asking the CDN for a source only as wide as the frame left them
 * visibly soft on retina. At this width there is headroom for both the crop and
 * a 2–3x display, and the files are still only a few KB.
 */
const REQUEST_PX = 160;

/**
 * A selected item's thumbnail, shared by the island circles, the list rows and
 * the modal's stacked discs so they all resolve the same way.
 *
 * Photos are cropped to fill the frame; a professional's logo is instead shown
 * whole, inset on its brand background — cropping a logo mangles it.
 */
export const SelectedItemMedia = ({
    item,
    className,
}: SelectedItemMediaProps) => {
    const isLogo = Boolean(item.imageIsLogo);

    return (
        <span
            className={cn(
                'block shrink-0 overflow-hidden bg-gray-100',
                className,
            )}
            style={
                isLogo && item.imageBackground
                    ? { backgroundColor: item.imageBackground }
                    : undefined
            }
        >
            {item.image ? (
                <Image
                    src={item.image}
                    alt={item.title}
                    width={REQUEST_PX}
                    height={REQUEST_PX}
                    fit={isLogo ? 'contain' : 'cover'}
                    fadeIn={false}
                    className={cn(
                        'h-full w-full',
                        isLogo ? 'object-contain p-1.5' : 'object-cover',
                    )}
                />
            ) : null}
        </span>
    );
};
